import { KeyValue } from "types/tenantConfig";

import { TagRowState } from "./types";

/**
 * Checks if a string is wrapped in double or single quotes
 */
export function hasWrappingQuotes(str?: string): boolean {
  if (!str || str.length < 2) return false;
  return (
    (str.startsWith('"') && str.endsWith('"')) ||
    (str.startsWith("'") && str.endsWith("'"))
  );
}

/**
 * Removes wrapping quotes from a string
 */
export function stripQuotes(str: string): string {
  if (hasWrappingQuotes(str)) {
    return str.slice(1, -1);
  }
  return str;
}

/**
 * Wraps a string in double quotes
 */
export function wrapInQuotes(str: string): string {
  return `"${str}"`;
}

/**
 * Parses an incoming KeyValue tag into internal TagRowState
 *
 * Logic:
 * - Both key and value quoted → exact match mode (strip quotes for display)
 * - Neither quoted → partial match mode (legacy behavior)
 * - One quoted → partial match mode (preserve quotes on that field)
 */
export function parseIncomingTag(tag: KeyValue): TagRowState {
  const keyHasQuotes = hasWrappingQuotes(tag.key);
  const valueHasQuotes = hasWrappingQuotes(tag.value);

  if (keyHasQuotes && (valueHasQuotes || !tag.value)) {
    // Both quoted (or key quoted with no value) → exact match mode, strip quotes for display
    return {
      key: stripQuotes(tag.key),
      value: stripQuotes(tag.value || ""),
      partialMatch: false,
    };
  }

  // All other cases → partial match (checked) - keep quotes as-is
  return {
    key: tag.key,
    value: tag.value || "",
    partialMatch: true,
  };
}

/**
 * Converts internal TagRowState to KeyValue for submission
 *
 * Logic:
 * - Partial match: submit as-is (user controls quotes)
 * - Exact match: auto-wrap in quotes if not already quoted
 */
export function generateOutputTag(row: TagRowState): KeyValue {
  const trimmedKey = row.key.trim();
  const trimmedValue = row.value.trim();

  if (row.partialMatch) {
    // Partial match: submit as-is
    return {
      key: trimmedKey,
      value: trimmedValue || undefined,
    };
  }

  // Exact match: auto-wrap in quotes if not already quoted
  return {
    key: wrapInQuotes(trimmedKey),
    value: trimmedValue ? wrapInQuotes(trimmedValue) : undefined,
  };
}

/**
 * Formats a tag for display in filter labels
 * Handles both KeyValue objects and other FilterValue types
 */
export function formatTagLabel(tag: KeyValue | unknown): string {
  // Handle non-object values (string, number, boolean, null)
  console.log(tag);
  if (tag === null || tag === undefined) {
    return "";
  }
  if (typeof tag !== "object") {
    return String(tag);
  }
  // Handle KeyValue objects
  const keyValue = tag as KeyValue;
  if (keyValue.key === undefined) {
    return "";
  }
  const key = keyValue.key;
  const value = keyValue.value || "";
  return value ? `${key}/${value}` : key;
}
